import { prettifyError, z, ZodError } from 'zod';

import type { Client } from './generated/client';

/** Rich error thrown by every API call — carries HTTP status and optional validation details. */
export class ApiError extends Error {
  /** Server prose or a validation report — for a human to read, not to branch on. */
  readonly details?: string;
  /**
   * Stable machine code from the API, when it sends one. Callers branch on
   * this rather than on the status alone: several different refusals share a
   * status, and the prose beside it is for humans and may be translated.
   */
  readonly code?: string;
  /**
   * Microsoft's `request-id`, present only when an upstream refused the call
   * and said so. Doubles as the marker that `message` is the upstream's own
   * prose rather than ours: that text is worth showing verbatim — it is the
   * only place the real reason is written — and it is the one message the app
   * cannot translate.
   */
  readonly requestId?: string;

  /**
   * The tail is an options object because `details` and `code` were adjacent
   * optional strings. Swapping them compiled — nothing about the types says
   * which is which — and reaching the second meant passing `undefined` as a
   * positional placeholder for the first, which one call site did.
   */
  constructor(
    message: string,
    public readonly status: number,
    public readonly path: string,
    {
      details,
      code,
      requestId,
    }: { details?: string; code?: string; requestId?: string } = {},
  ) {
    super(message);
    this.name = 'ApiError';
    this.details = details;
    this.code = code;
    this.requestId = requestId;
  }
}

/**
 * Tolerant field by field, and only then as a whole.
 *
 * The `.catch({})` used to sit on the object alone, so one unexpected field cost
 * every other one. That is reachable: `HttpExceptionFilter` puts the *entire*
 * exception payload into `message` when the payload has no `message` of its own,
 * so a `ForbiddenException({ code })` thrown without prose arrives as
 * `message: { code }` — an object where a string was declared. The parse failed,
 * `{}` came back, and the `code` several commits exist to deliver was gone.
 *
 * Per-field `.catch` degrades that one field. The object-level one is now the
 * last resort it should always have been: a body that is not an object at all
 * (`null`, an array, a bare string) has nothing to salvage, and both callers
 * below are then free to `parse` without guarding against a throw.
 */
const errorBodySchema = z
  .object({
    message: z.string().optional().catch(undefined),
    code: z.string().optional().catch(undefined),
    requestId: z.string().optional().catch(undefined),
  })
  .loose()
  .catch({});

/**
 * Pull the API's machine code out of an error body, if there is one.
 *
 * @internal Test seam over `errorBodySchema`, which the adapter below uses.
 */
export function errorCodeOf(details: string | undefined): string | undefined {
  if (!details) {
    return undefined;
  }
  try {
    return errorBodySchema.parse(JSON.parse(details)).code;
  } catch {
    return undefined;
  }
}

/**
 * Turn every failure of the generated hey-api client into an `ApiError`, so
 * the contract the app branches on survives the client swap unchanged:
 * `routes/_auth.tsx` reads `instanceof ApiError`, `err.code` and `err.status`;
 * the photo query treats a 404 as "no photo"; several components read
 * `err.message` for a machine code the server put there.
 *
 * Three failure shapes arrive here:
 *  - non-2xx: the client throws the parsed JSON body (an object) or the raw
 *    body text (a string) — mapped to status/message/code/details;
 *  - a 2xx whose body fails the generated zod response validator — mapped to
 *    the same "Invalid response from server" ApiError `api.json()` used to
 *    throw, with the prettified report in `details`;
 *  - no response at all (network failure, token acquisition, abort) — left
 *    untouched, so `instanceof ApiError` keeps meaning "the server answered".
 */
export function installApiErrorAdapter(client: Client): void {
  client.interceptors.error.use((error, response, request) => {
    if (error instanceof ApiError || !response) {
      return error;
    }

    const path = request ? new URL(request.url).pathname : 'unknown';

    if (error instanceof ZodError) {
      return new ApiError(
        'Invalid response from server',
        response.status,
        path,
        {
          details: prettifyError(error),
        },
      );
    }

    const details =
      typeof error === 'string' ? error : JSON.stringify(error ?? {});
    const body = errorBodySchema.parse(
      typeof error === 'object' && error !== null ? error : {},
    );
    return new ApiError(
      body.message ?? `HTTP ${response.status}`,
      response.status,
      path,
      { details, code: body.code, requestId: body.requestId },
    );
  });
}
